import { useMemo, useState } from 'react'
import { Scale, ShieldAlert } from 'lucide-react'
import { useTrademarks } from '../../hooks/useCompetitiveData'
import HelpTooltip from './HelpTooltip'

function formatDate(value: string | null | undefined): string {
  if (!value) return 'Not available'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

function scoreBadgeClass(score: number): string {
  if (score >= 70) return 'border-[var(--danger-100)] bg-[var(--danger-50)] text-[var(--danger-700)]'
  if (score >= 50) return 'border-[var(--warning-100)] bg-[var(--warning-50)] text-[var(--warning-700)]'
  return 'border-[var(--brand-100)] bg-[var(--brand-50)] text-[var(--brand-700)]'
}

export default function TrademarkRadarPanel() {
  const [expandedGoods, setExpandedGoods] = useState<Record<string, boolean>>({})
  const { data, isLoading, error } = useTrademarks()
  const errorMessage = error instanceof Error ? error.message : null

  const filings = useMemo(
    () => [...(data?.filings ?? [])].sort((left, right) => Number(right.relevance_score) - Number(left.relevance_score)),
    [data?.filings]
  )
  const topFilings = filings.slice(0, 3)
  const remainingCount = Math.max(0, filings.length - topFilings.length)

  return (
    <section className="rounded-2xl border border-[var(--patterniq-navy-200)] bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5">
            <h3 className="competitive-panel-title">Trademark Radar</h3>
            <HelpTooltip
              title="Trademark Radar"
              text="We scan new USPTO filings in cosmetics/skincare categories weekly."
            />
          </div>
          <p className="competitive-body-text mt-1">
            New IC-003 filings can indicate competitors 6-12 months before Amazon launch.
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded-xl border border-[var(--patterniq-navy-200)] bg-[var(--patterniq-navy-50)] p-3">
          <p className="competitive-support-label">Total Filings</p>
          <p className="mt-1 text-2xl font-bold text-[var(--patterniq-navy-900)]">
            {data?.summary?.total_filings ?? 0}
          </p>
        </div>
        <div className="rounded-xl border border-[var(--warning-100)] bg-[var(--warning-50)] p-3">
          <p className="competitive-support-label">High Relevance (50+)</p>
          <p className="mt-1 text-2xl font-bold text-[var(--warning-700)]">
            {data?.summary?.high_relevance ?? 0}
          </p>
        </div>
        <div className="rounded-xl border border-[var(--brand-100)] bg-[var(--brand-50)] p-3">
          <p className="competitive-support-label">New This Week</p>
          <p className="mt-1 text-2xl font-bold text-[var(--brand-700)]">{data?.summary?.new_this_week ?? 0}</p>
        </div>
      </div>

      {errorMessage && (
        <div className="mt-4 rounded-lg border border-[var(--danger-100)] bg-[var(--danger-50)] p-3 text-sm text-[var(--danger-700)]">
          {errorMessage}
        </div>
      )}

      {isLoading ? (
        <div className="mt-4 rounded-lg border border-[var(--patterniq-navy-200)] bg-[var(--patterniq-navy-50)] p-4 text-sm text-[var(--patterniq-navy-600)]">
          Loading trademark filings...
        </div>
      ) : topFilings.length === 0 ? (
        <div className="mt-4 rounded-lg border border-[var(--patterniq-navy-200)] bg-[var(--patterniq-navy-50)] p-4 text-sm text-[var(--patterniq-navy-700)]">
          No trademark filings available yet.
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {topFilings.map((filing) => {
            const relevance = Number(filing.relevance_score) || 0
            const showGoods = Boolean(expandedGoods[filing.serial_number])
            return (
              <article
                key={filing.serial_number}
                className="rounded-xl border border-[var(--patterniq-navy-200)] bg-[var(--patterniq-navy-50)] p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h4 className="text-base font-semibold text-[var(--patterniq-navy-900)]">
                      {filing.mark_text}
                    </h4>
                    <p className="mt-0.5 text-sm text-[var(--patterniq-navy-700)]">
                      Applicant: {filing.applicant_name}
                    </p>
                  </div>
                  <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${scoreBadgeClass(relevance)}`}>
                    Relevance {Math.round(relevance)}
                  </span>
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-[var(--patterniq-navy-600)]">
                  <span>Filed {formatDate(filing.filing_date)}</span>
                  <span className="rounded-full border border-[var(--patterniq-navy-200)] bg-white px-2 py-0.5 capitalize">
                    {filing.status}
                  </span>
                </div>

                <p className="mt-3 flex items-start gap-1.5 text-xs text-[var(--danger-700)]">
                  <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  Why it matters: this class-003 filing could become an Amazon competitor within 6-12 months.
                </p>

                <button
                  type="button"
                  onClick={() =>
                    setExpandedGoods((prev) => ({
                      ...prev,
                      [filing.serial_number]: !prev[filing.serial_number],
                    }))
                  }
                  className="mt-2 text-xs text-blue-600 hover:underline"
                >
                  {showGoods ? 'Hide goods/services ▲' : 'Show goods/services ▸'}
                </button>

                {showGoods && (
                  <p className="mt-2 text-xs text-[var(--patterniq-navy-600)]">
                    {filing.goods_desc || 'No goods/services description provided.'}
                  </p>
                )}
              </article>
            )
          })}

          {remainingCount > 0 && (
            <p className="mt-2 text-xs text-slate-500">
              + {remainingCount} more filings ·{' '}
              <a href="#" className="text-blue-600 hover:underline">
                View all →
              </a>
            </p>
          )}
        </div>
      )}

      <p className="mt-3 flex items-center gap-1.5 text-xs text-[var(--patterniq-navy-500)]">
        <Scale className="h-3.5 w-3.5" />
        Last update: {formatDate(data?.last_updated)}
      </p>
    </section>
  )
}
